import React, { useEffect, useRef, useState } from 'react';
import SchoolPicker from './SchoolPicker';
import CollegePicker from './CollegePicker';
import { US_STATES, CA_PROVINCES } from '../constants/addressRegions';

const ELIGIBILITY_YEARS = [1, 2, 3, 4, 5];

/**
 * The athlete's school block: a high school (picker, city, state/province,
 * postal code) or, behind the "I currently play in college" toggle, a college
 * (picker + years of eligibility remaining). The caller owns persistence and
 * error display: `onSave(fields)` may return a promise, and `false` means the
 * save failed.
 *
 * The toggle is LOCAL draft state. The server needs the eligibility when it
 * switches an athlete to college, so nothing is saved until a college AND an
 * eligibility value are both chosen; then ONE request carries
 * {school_level, college_name, college_unitid, eligibility_years_remaining}.
 * Switching back saves {school_level: 'high_school'} only if the stored level
 * is 'college' (after `confirmLeaveCollege()` resolves true, when given); the
 * server clears the rest.
 *
 * college_unitid is sent only when college_name is being set: a list pick sends
 * its unitid, free text sends an explicit null.
 *
 * The admin/partner athlete editor reuses this with its own wording and its own
 * high school picker: `highSchool` replaces the built-in high school fields,
 * and `toggleLabel` / `saveHint` replace the athlete-voiced text.
 */
export default function SchoolLevelSection({
  profile, onSave, onModeChange, confirmLeaveCollege,
  highSchool, toggleLabel = 'I currently play in college',
  saveHint = 'Choose your college and years of eligibility to save.',
}) {
  const storedCollege = profile.school_level === 'college';
  const isCanada = profile.country === 'CA';
  const regions = isCanada ? CA_PROVINCES : US_STATES;

  const [draftCollege, setDraftCollege] = useState(storedCollege);
  useEffect(() => { setDraftCollege(storedCollege); }, [storedCollege]);
  const modeCb = useRef(onModeChange);
  modeCb.current = onModeChange;
  useEffect(() => { if (modeCb.current) modeCb.current(draftCollege); }, [draftCollege]);

  // ---- high school ----
  const [query, setQuery] = useState(profile.high_school || '');
  useEffect(() => { setQuery(profile.high_school || ''); }, [profile.high_school]);

  const [hs, setHs] = useState({
    city: profile.high_school_city || '',
    state: profile.high_school_state || '',
    zip: profile.high_school_zip || '',
  });
  useEffect(() => {
    setHs({
      city: profile.high_school_city || '',
      state: profile.high_school_state || '',
      zip: profile.high_school_zip || '',
    });
  }, [profile.high_school_city, profile.high_school_state, profile.high_school_zip]);

  // Saves school fields, and only AFTER the save succeeds applies the matching
  // local city/zip/state; on failure the box rolls back to the saved school so
  // the screen never shows values the server did not keep.
  const saveSchool = async (fields) => {
    const ok = await onSave(fields);
    if (ok === false) {
      setQuery(profile.high_school || '');
      return;
    }
    setHs(prev => ({
      city: 'high_school_city' in fields ? fields.high_school_city : prev.city,
      zip: 'high_school_zip' in fields ? fields.high_school_zip : prev.zip,
      state: fields.high_school_state || prev.state,
    }));
  };

  const handleSchoolChange = ({ name, city, state, zip }) => {
    setQuery(name);
    if (city || state || zip) {               // a pick: all four fields
      saveSchool({ high_school: name, high_school_city: city, high_school_state: state, high_school_zip: zip });
    } else if (name === '' && profile.high_school) {
      handleSchoolCommitText('');             // the clear (x) button: same path as an emptied box
    }
    // otherwise typing: nothing saved, province kept
  };

  // Free text keeps the PROVINCE (defaulting to the athlete's address province
  // when none is saved and a NAME is being saved). When it replaces a different
  // saved school name, the old school's city and zip go with it (a hybrid "new
  // name in the old city" record is the bug commit 876d3ab3 fixed). A first entry
  // keeps whatever city/zip are already on the form. Clearing the box sends
  // exactly name + city + zip as '' and never a defaulted province.
  const handleSchoolCommitText = (text) => {
    const saved = profile.high_school || '';
    if (text === saved) return;
    if (text === '') {
      saveSchool({ high_school: '', high_school_city: '', high_school_zip: '' });
      return;
    }
    const fields = { high_school: text };
    if (saved) {
      fields.high_school_city = '';
      fields.high_school_zip = '';
    }
    if (!profile.high_school_state && profile.state) fields.high_school_state = profile.state;
    saveSchool(fields);
  };

  const saveHsField = (field, value) => {
    if (value !== (profile[field] || '')) onSave({ [field]: value });
  };

  // ---- college ----
  const [collegeText, setCollegeText] = useState(profile.college_name || '');
  const [collegeName, setCollegeName] = useState(profile.college_name || '');
  const [collegeUnitid, setCollegeUnitid] = useState(profile.college_unitid ?? null);
  const [eligibility, setEligibility] = useState(
    profile.eligibility_years_remaining ? String(profile.eligibility_years_remaining) : '');
  useEffect(() => {
    setCollegeText(profile.college_name || '');
    setCollegeName(profile.college_name || '');
    setCollegeUnitid(profile.college_unitid ?? null);
  }, [profile.college_name, profile.college_unitid]);
  useEffect(() => {
    setEligibility(profile.eligibility_years_remaining ? String(profile.eligibility_years_remaining) : '');
  }, [profile.eligibility_years_remaining]);

  const commitCollege = (name, unitid) => {
    setCollegeText(name);
    setCollegeName(name);
    setCollegeUnitid(unitid);
    if (storedCollege) {
      onSave({ college_name: name, college_unitid: unitid });
    } else if (eligibility) {
      onSave({
        school_level: 'college', college_name: name, college_unitid: unitid,
        eligibility_years_remaining: Number(eligibility),
      });
    }
  };

  const handleCollegePick = (college) => commitCollege(college.n, college.u ?? null);

  const handleCollegeCommitText = (text) => {
    if (!text.trim() || text === collegeName) return;   // untouched or empty: never null the unitid
    commitCollege(text, null);
  };

  const handleEligibility = (e) => {
    const value = e.target.value;
    setEligibility(value);
    if (!value) return;
    if (storedCollege) {
      onSave({ eligibility_years_remaining: Number(value) });
    } else if (collegeName) {
      onSave({
        school_level: 'college', college_name: collegeName, college_unitid: collegeUnitid,
        eligibility_years_remaining: Number(value),
      });
    }
  };

  const [pending, setPending] = useState(false);
  const handleToggle = async (e) => {
    if (pending) return;
    const on = e.target.checked;
    if (on || !storedCollege) {
      setDraftCollege(on);
      return;
    }
    // Leaving a saved college wipes college, unitid and eligibility on the
    // server with no undo: confirm first, and ignore toggles while it is in flight.
    setPending(true);
    try {
      if (confirmLeaveCollege && !(await confirmLeaveCollege())) return;
      const ok = await onSave({ school_level: 'high_school' });
      if (ok !== false) setDraftCollege(false);
    } finally {
      setPending(false);
    }
  };

  const regionLabel = isCanada ? 'Province' : 'State';
  const postalLabel = isCanada ? 'Postal Code' : 'Zip Code';
  const regionOptions = hs.state && !regions.includes(hs.state) ? [hs.state, ...regions] : regions;
  const needsMore = draftCollege && !storedCollege && (!collegeName || !eligibility);

  return (
    <div className="school-level">
      <label className="settings-field__checkbox-label school-level__toggle">
        <input
          type="checkbox"
          id="school_level_college"
          name="school_level_college"
          checked={draftCollege}
          disabled={pending}
          onChange={handleToggle}
        />
        <span>{toggleLabel}</span>
      </label>

      {!draftCollege ? (highSchool || (
        <>
          <div>
            <div className="form-field" style={{ position: 'relative' }}>
              <label className="form-field__label" htmlFor="high_school">High School</label>
              <SchoolPicker
                inputId="high_school"
                value={query}
                city={hs.city}
                state={hs.state}
                zip={hs.zip}
                onChange={handleSchoolChange}
                onCommitText={handleSchoolCommitText}
                preferState={profile.state}
                className="form-field__input"
                placeholder="Start typing school name..."
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label className="form-field__label" htmlFor="high_school_city">City</label>
              <input
                id="high_school_city"
                type="text"
                name="high_school_city"
                value={hs.city}
                onChange={(e) => setHs(prev => ({ ...prev, city: e.target.value }))}
                onBlur={(e) => saveHsField('high_school_city', e.target.value)}
                className="form-field__input"
              />
            </div>
            <div className="form-row-nested">
              <div className="form-field">
                <label className="form-field__label" htmlFor="high_school_state">{regionLabel}</label>
                <select
                  id="high_school_state"
                  name="high_school_state"
                  value={hs.state}
                  onChange={(e) => setHs(prev => ({ ...prev, state: e.target.value }))}
                  onBlur={(e) => saveHsField('high_school_state', e.target.value)}
                  className="form-field__select"
                >
                  <option value="">Select...</option>
                  {regionOptions.map(st => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label className="form-field__label" htmlFor="high_school_zip">{postalLabel}</label>
                <input
                  id="high_school_zip"
                  type="text"
                  name="high_school_zip"
                  value={hs.zip}
                  onChange={(e) => setHs(prev => ({ ...prev, zip: e.target.value }))}
                  onBlur={(e) => saveHsField('high_school_zip', e.target.value)}
                  className="form-field__input"
                />
              </div>
            </div>
          </div>
        </>
      )) : (
        <>
          <div className="form-row">
            <div className="form-field" style={{ position: 'relative' }}>
              <label className="form-field__label" htmlFor="college_name">College</label>
              <CollegePicker
                inputId="college_name"
                value={collegeText}
                onInputChange={setCollegeText}
                onSelect={handleCollegePick}
                onCommitText={handleCollegeCommitText}
                className="form-field__input"
              />
            </div>
            <div className="form-field">
              <label className="form-field__label" htmlFor="eligibility_years_remaining">Years of eligibility remaining</label>
              <select
                id="eligibility_years_remaining"
                name="eligibility_years_remaining"
                value={eligibility}
                onChange={handleEligibility}
                className="form-field__select"
              >
                <option value="">Select...</option>
                {ELIGIBILITY_YEARS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
          {needsMore && (
            <p className="school-level__hint" role="status">
              {saveHint}
            </p>
          )}
        </>
      )}
    </div>
  );
}
